import React from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { uniqBy } from "lodash";
import { ConnectionHandler, type RecordProxy } from "relay-runtime";

import QuotedItem from "~/actionItems/QuotedItem";
import { ActionItemLinkageModalRenderer } from "~/common/ActionItemLinkageModal";
import StreamSelect, { type StreamOrEventType } from "~/common/StreamSelect";
import { commitMutationPromise } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal, Switch } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toasty, { ToastActionButton } from "~/ui/toast";
import Tooltip from "~/ui/Tooltip";

import useActionItemGroupItemsKey from "./hooks/useActionItemsGroupItemsKey";

import type { CopyToModalBlock$key } from "~/__generated__/CopyToModalBlock.graphql";
import type { CopyToModalCopyBlocksMutation } from "~/__generated__/CopyToModalCopyBlocksMutation.graphql";
import type { CopyToModalQuery } from "~/__generated__/CopyToModalQuery.graphql";

import styles from "./CopyToModal.module.css";

const copyBlocksToMutation = graphql`
	mutation CopyToModalCopyBlocksMutation($ids: [ID!]!, $toIds: [ID!]!, $link: Boolean, $withNestedBlocks: Boolean) {
		copyActionItems(ids: $ids, toIds: $toIds, link: $link, withNestedBlocks: $withNestedBlocks) {
			newBlocks {
				...ActionItemData @relay(mask: false)
			}
		}
	}
`;

const CopyToModalBlockFragment = graphql`
	fragment CopyToModalBlock on NoteBlock @relay(plural: true) {
		id
		type

		...QuotedItemBlock
		descendants {
			type
		}
	}
`;

interface CopyToModalProps {
	items: CopyToModalBlock$key;
	showCompleted?: boolean;
	showWontDo?: boolean;
	showCreatedByYou?: boolean;
	showRecentOnly?: boolean;
	showAiDetected?: boolean;
	teammatesView?: boolean;
	assignedTo?: string[];
	forStream?: string;
	forNote?: string;
	forRecording?: string;
	close: () => void;
}

export default function CopyToModal({
	close,
	items: itemsData,
	showCompleted = false,
	showWontDo = false,
	showCreatedByYou = false,
	showRecentOnly = false,
	showAiDetected = true,
	teammatesView = false,
	assignedTo,
	forStream,
	forNote,
	forRecording,
}: CopyToModalProps) {
	const items = useFragment(CopyToModalBlockFragment, itemsData);
	const hasDescendants = items.some(v => v.descendants.length !== 0);

	// The only scenerio where multiple items are passed is when this is called from the action items
	// section. In that case all of them will be actions items and the itemName will be "Action Item".
	// When copying a single block from a note, the itemName match the type of that block
	// regardless of their descendants.
	const allParentsAreActionItems = items.every(v => v.type === "todo");
	const allParentsAreTalkingPoints = items.every(v => v.type === "agenda");
	const someAreActionItemsIncludingDescendants = items.some(
		v => v.type === "todo" || v.descendants.some(d => d.type === "todo"),
	);

	const itemName = allParentsAreActionItems ? "Action Item" : "Talking Point";
	const showIncludeChildrenSwitch = hasDescendants;

	const [selectedStreams, setSelectedStreams] = React.useState<string[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [withNestedBlocks, setIncludeChildren] = React.useState(true);

	// show the link option if all parents are action items,
	// or all parents are talking points, some of its descendants are action items and we are including them
	const showLinkOption =
		allParentsAreActionItems ||
		(allParentsAreTalkingPoints && someAreActionItemsIncludingDescendants && withNestedBlocks);

	const [isLinked, setIslinked] = React.useState(showLinkOption);
	const onIncludeChildrenSwitchChange = React.useCallback(() => setIncludeChildren(v => !v), [setIncludeChildren]);
	const onStreamSelected = React.useCallback((selected: StreamOrEventType[]) => {
		setSelectedStreams(selected.map(({ id }) => id || "").filter(Boolean));
	}, []);

	const onIsLinkedSwitchChange = React.useCallback(() => {
		setIslinked(v => !v);
	}, [setIslinked]);
	const onViewStream = React.useCallback(() => {
		if (items.length === 1) {
			const item = items[0];
			__globalModalContext?.open(({ close }) => <ActionItemLinkageModalRenderer blockId={item.id} close={close} />);
		}
	}, [items]);

	const itemsKey = useActionItemGroupItemsKey();
	const onConfirm = React.useCallback(() => {
		setLoading(true);
		commitMutationPromise<CopyToModalCopyBlocksMutation>(environment, {
			mutation: copyBlocksToMutation,
			variables: {
				ids: items.map(item => item.id),
				toIds: selectedStreams,
				link: showLinkOption ? isLinked : false,
				withNestedBlocks: withNestedBlocks,
			},
			updater: (store, response) => {
				if (!response) return;
				if (!response.copyActionItems?.newBlocks) {
					return;
				}
				const rootRecord = store.getRoot();
				if (!rootRecord) {
					return;
				}
				let defaultGroupItemsConnection: RecordProxy | undefined;
				const actionItemGroupsRecord = ConnectionHandler.getConnection(
					rootRecord,
					"ActionItemGroupsList_actionItemGroups",
				);
				if (actionItemGroupsRecord) {
					const actionItemGroups = (actionItemGroupsRecord.getLinkedRecords("edges") ?? []).map(e =>
						e.getLinkedRecord("node"),
					);
					for (let i = 0; i < actionItemGroups.length; i++) {
						if (actionItemGroups[i]?.getValue("isDefaultGroup")) {
							defaultGroupItemsConnection = actionItemGroups[i]?.getLinkedRecord(itemsKey);
							break;
						}
					}
				}

				const syntheticConnectionRecord = ConnectionHandler.getConnection(
					rootRecord,
					"SyntheticActionItemGroupsList_actionItems",
					{
						includeCompleted: showCompleted,
						includeWontDo: showWontDo,
						includeOnlyCreatedByYou: showCreatedByYou,
						includeRecentOnly: showRecentOnly,
						includeAiDetected: !showAiDetected,
						forNote: forNote,
						forRecording: forRecording,
						teammatesView,
						assignedTo,
						forStream,
					},
				);
				response.copyActionItems.newBlocks.forEach(newBlock => {
					if (!newBlock) return;
					const actionItemRecord = store.get(newBlock.id);
					if (!actionItemRecord) {
						return;
					}
					// Add item to the default group
					if (defaultGroupItemsConnection) {
						const edge = ConnectionHandler.createEdge(
							store,
							defaultGroupItemsConnection,
							actionItemRecord,
							"NoteBlock",
						);
						ConnectionHandler.insertEdgeBefore(defaultGroupItemsConnection, edge);
					}
					// Add item to synthetic action items list
					if (syntheticConnectionRecord) {
						const edge = ConnectionHandler.createEdge(store, syntheticConnectionRecord, actionItemRecord, "NoteBlock");
						ConnectionHandler.insertEdgeBefore(syntheticConnectionRecord, edge);
					}
				});
			},
		})
			.then(resp => {
				if (!resp || !resp.copyActionItems?.newBlocks) {
					toasty.error("Something went wrong when copying items.");
					return;
				}
				const streams = uniqBy(
					resp.copyActionItems.newBlocks.map(b => b?.note?.stream),
					s => s?.id,
				);
				toasty.success(
					streams.length > 1 ? "Multiple note series" : streams[0]?.displayName || "Untitled note series",
					{
						title: `${items.length === 1 ? itemName : `${items.length} ${itemName.toLowerCase()}s`} copied${
							isLinked ? " and linked" : ""
						} in:`,
						actions:
							items.length === 1 && isLinked ? (
								<ToastActionButton onClick={onViewStream}>View</ToastActionButton>
							) : null,
					},
				);
				close();
			})
			.catch(e => {
				if (e instanceof Array && e.length > 0 && "message" in e[0]) {
					toasty.error(e[0].message);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [
		isLinked,
		selectedStreams,
		close,
		items,
		onViewStream,
		setLoading,
		itemName,
		showLinkOption,
		showCompleted,
		showWontDo,
		showCreatedByYou,
		showRecentOnly,
		showAiDetected,
		forNote,
		forRecording,
		teammatesView,
		assignedTo,
		forStream,
		withNestedBlocks,
		itemsKey,
	]);
	const preview =
		items.length !== 1 ? null : (
			<div className={styles.copyToPreview}>
				<span className={styles.smallHeader}>Preview: </span>
				<QuotedItem block={items[0]} showAsLinked={isLinked} withNestedBlocks={withNestedBlocks} />
			</div>
		);

	return (
		<Modal onClose={close}>
			<Modal.Header onClose={close}>
				<Modal.Title>
					Copy {itemName}
					{items.length > 1 ? "s" : ""}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.promptBody}>
				<div className={styles.streamSelect}>
					<div className={styles.smallHeader}>
						<span>Copy {items.length > 1 && <b>({items.length})</b>} to...</span>
						<span>{selectedStreams.length > 1 ? <>{selectedStreams.length} Streams</> : null}</span>
					</div>
					<StreamSelect
						autoFocus={true}
						onChange={onStreamSelected}
						placeholder="Search for Meetings, 1:1s or other note series"
						isMulti={true}
					/>
				</div>
				<div>
					{showLinkOption && (
						<div className={styles.isToggle}>
							<label>
								<span>Make action items linked copies</span>
								<div className={styles.infoIcon}>
									<Tooltip
										content={"A linked copy allows you to complete all instances of this Action Item with one check."}
										placement="right"
										maxWidth="11.25rem"
										appendTo={document.body}
									>
										<FontAwesomeIcon icon={faInfoCircle} />
									</Tooltip>
								</div>
								<div className={styles.toggle}>
									<Switch checked={isLinked} onCheckedChange={onIsLinkedSwitchChange} size="medium" />
								</div>
							</label>
						</div>
					)}

					{showIncludeChildrenSwitch && (
						<div className={styles.isToggle}>
							<label>
								<span>Include children</span>
								<Switch
									checked={withNestedBlocks}
									onCheckedChange={onIncludeChildrenSwitchChange}
									size="medium"
									className={styles.toggle}
								/>
							</label>
						</div>
					)}
				</div>
				{preview}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={close}>
					Cancel
				</Button>
				<Button variant="primary" size="normal" onClick={onConfirm} disabled={!selectedStreams.length || loading}>
					Copy {itemName}
					{items.length > 1 ? "s" : ""}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

const copyToModalQuery = graphql`
	query CopyToModalQuery($ids: [BlockID!]!) {
		viewer {
			noteBlocks(ids: $ids) {
				...CopyToModalBlock
			}
		}
	}
`;

type BlockID = string | { noteId: string; key: string };
function CopyToModalRendererInner({
	ids,
	close,
	...modalProps
}: {
	ids: readonly BlockID[];
	close: () => void;
	showCompleted?: boolean;
	showWontDo?: boolean;
	showCreatedByYou?: boolean;
	showRecentOnly?: boolean;
	showAiDetected?: boolean;
	teammatesView?: boolean;
	assignedTo?: string[];
	forStream?: string;
}) {
	const props = useLazyLoadQuery<CopyToModalQuery>(
		copyToModalQuery,
		{ ids: ids.map(id => (typeof id === "string" ? { id } : { noteId: id.noteId, blockKey: id.key })) },
		{ fetchPolicy: "network-only" },
	);

	return <CopyToModal items={props.viewer.noteBlocks} close={close} {...modalProps} />;
}

export const CopyToModalRenderer = withSuspense(null, CopyToModalRendererInner);
